import { KEY_CONFIG } from './../../config/key-config';
import {
  Component, Input, Output, EventEmitter, OnInit, ElementRef, HostListener, forwardRef, ViewEncapsulation,
  OnChanges, SimpleChanges
} from '@angular/core';
import { map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { DropdownModel } from './dropdown.model';

export const NO_OPTION = -1;
export const FIRST_OPTION = 0;

/* Custom control value accessor added to integrated dropdown component with form framework */
export const APP_DROPDOWN_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DropdownComponent),
  multi: true
};

@Component({
  selector: 'app-dropdown',
  styleUrls: ['./dropdown.component.scss'],
  templateUrl: './dropdown.component.html',
  providers: [APP_DROPDOWN_CONTROL_VALUE_ACCESSOR],
  encapsulation: ViewEncapsulation.None
})

export class DropdownComponent implements OnInit, ControlValueAccessor, OnChanges {
  @Input() placeholderAriaLabel: any;

  @Input() labelId: any;

  @Input() options: DropdownModel[];

  @Input() prePopulateValue: DropdownModel;

  @Input() disabled: boolean = false;

  @Input() placeholder: string;

  @Input() inputId: string;

  @Output() selectEmit: EventEmitter<DropdownModel>;

  propagateChange: any = () => { }; /** call back method for  registerOnChange function */
  dropDownValue: DropdownModel;
  configuration: any[];
  showDropDown: boolean;
  operatingSystem: Array<DropdownModel>;
  dropDown: DropdownModel;
  flag: Boolean = false;
  onChange: any;
  onTouched: any;
  option: number;
  inputNotNeeded: boolean = false;
  screenReaderText: any = null;
  pressEnterId: any;
  
  private closeDropdownBind: EventListener = this.closeDropdownHandler.bind(this);

  constructor(protected router: Router, private el: ElementRef) { 
    this.showDropDown = false;
    this.option = NO_OPTION;
    this.selectEmit = new EventEmitter<DropdownModel>();
  }

  /** Function to open Dropdown. Dropdown option count is initialized to -1. click event listener added to close dropdown when clicked outside */
  openDropDown() {
    this.showDropDown = true;
    this.option = NO_OPTION;
    window.document.addEventListener('click', this.closeDropdownBind);
    const labelElement = document.getElementById(this.labelId);
    if (labelElement) {
      labelElement.setAttribute('aria-hidden', 'true');
    }
  }

  /** Function to open Dropdown. Dropdown option count is initialized to -1. click event listener removed */
  closeDropDown() {
    if(this.showDropDown && this.inputId){
     document.getElementById(this.inputId).focus();
    }
    else if(this.showDropDown && !this.inputId) {
      document.getElementById("selectWrapId").focus();
    }
    this.showDropDown = false;
    this.option = NO_OPTION;
    const labelElement = document.getElementById(this.labelId);
    if (labelElement) {
      labelElement.removeAttribute('aria-hidden');
    }
    window.document.removeEventListener('click', this.closeDropdownBind); 
  }

  /** On click handler to select options. */
  selectItem(value: DropdownModel, closeDropDowns: boolean) {
    if (this.disabled) {
      return;
    }
    if(closeDropDowns) {
      this.closeDropDown();
    }
    this.dropDownValue = value;
    this.propagateChange(value);
    this.selectEmit.emit(value);
  }

  /**Toggle method to open and close dropdown */
  toggle() {
    if (this.disabled) {
      return;
    }
    if (this.showDropDown) {
      this.closeDropDown();
    } else {
      this.openDropDown();
    }
  }

  /** ControlValueAccessor impemented method. Used to prepopulate dropdown */
  writeValue(value: any): void {
    if (!this.options) {
      Promise.resolve(null).then(() => this.writeValue(value));
      return;
    }
    this.dropDownValue = value;
  }

  /**ControlValueAccessor implemented method. used to propagate changes to parent's form control */
  registerOnChange(fn: (value: any) => void): void {
    this.propagateChange = fn;
  }

  /**ControlValueAccessor impemented method */
  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  /**Handler for Keydown events. For CATO
   * SPACE and ENTER key down will open dropdown.
   * SPACE and ENTER key down will select options
   * UP and DOWN ARROW used to navigate through options.
   */
  handleSpanKeydown(event, source): void {
    if (event.keyCode !== KEY_CONFIG.tabKey) {
      event.preventDefault();
      event.stopPropagation();
      if (event.keyCode === KEY_CONFIG.space || event.keyCode === KEY_CONFIG.enter || (event.keyCode === KEY_CONFIG.arrowDown && !this.showDropDown)) {
        this.handleSpaceAndEnterKeyEvent(source)
      }
      else if (event.keyCode >= KEY_CONFIG.firstSmallAlphabet && event.keyCode <= KEY_CONFIG.lastSmallAlphabet) {
        this.handleSearchEvent(event.key);
      }
      else if (event.keyCode === KEY_CONFIG.arrowDown) {
        this.handleArrowDownKeyEvent();
      } else if (event.keyCode === KEY_CONFIG.arrowUp) {
        this.handleArrowUpKeyEvent();
      }
    } else {
      this.closeDropDown();
    }
  }

  handleSearchEvent(source: string) {
    for (let i = 1; i < this.options.length; i++) {
      if (source == this.options[i].value[0] || source.toUpperCase() == this.options[i].value[0]) {
        this.option = i;
        let currentLi = this.el.nativeElement.children[0].children[1].children[this.option];
        if (currentLi) {
          currentLi.focus();
          this.el.nativeElement.children[0].children[0].innerHTML = this.options[this.option].name;
          this.selectItem(this.options[this.option], false);
        }
        break;
      }
    }
  }

  /**Handler for Space and Enter Key down events */
  handleSpaceAndEnterKeyEvent(source) {
    if (source === 'list') {
      if (this.option !== NO_OPTION) {
        this.selectItem(this.options[this.option], true);
      } else {
        this.toggle();
      }
    } else {
      this.toggle();
    }

  }

  /**Handler for Arrow Key down event */
  handleArrowDownKeyEvent() {
    if (!this.showDropDown) {
      this.option = this.options.findIndex(opt => opt.name === this.el.nativeElement.children[0].children[0].innerHTML);
      if (this.option === NO_OPTION) {
        this.option = FIRST_OPTION;
      } else {
        if (this.option === this.options.length - 1) {
          return;
        } else {
          this.option++;
        }
      }
      // this.processArrowEvent();
    }
    else if (this.showDropDown) {
      if (this.option === NO_OPTION) {
        this.option = FIRST_OPTION;
      } else {
        if (this.option === this.options.length - 1) {
          return;
        } else {
          this.option++;
        }
      }
      let currentLi = this.el.nativeElement.children[0].children[1].children[this.option];
      if (currentLi) {
        currentLi.focus();
        this.el.nativeElement.children[0].children[0].innerHTML = this.options[this.option].name;
      }
    }
  }

  /**Handler for Arrow Up key down events */
  handleArrowUpKeyEvent() {
    if (!this.showDropDown) {
      this.option = this.options.findIndex(opt => opt.name === this.el.nativeElement.children[0].children[0].innerHTML);
      if (this.option === NO_OPTION) {
        return;
      } else {
        if (this.option === FIRST_OPTION) {
          return;
        } else {
          this.option--;
        }
      }
      // this.processArrowEvent();
    }
    else if (this.showDropDown) {
      if (this.option === NO_OPTION) {
        return;
      } else {
        if (this.option === FIRST_OPTION) {
          return;
        } else {
          this.option--;
        }
      }
      let currentLi = this.el.nativeElement.children[0].children[1].children[this.option];
      if (currentLi) {
        currentLi.focus();
        this.el.nativeElement.children[0].children[0].innerHTML = this.options[this.option].name;
      }
    }
  }

  processArrowEvent() {
    let currentLi = this.el.nativeElement.children[0].children[0];
    this.el.nativeElement.children[0].children[0].innerHTML = this.options[this.option].name;
    // this.el.nativeElement.children[0].children[0].
    this.el.nativeElement.children[0].children[0].attributes['aria-label'].value = this.option+1+'of'+this.options.length+this.placeholderAriaLabel+this.options[this.option].name;
    this.el.nativeElement.children[0].children[0].attributes['aria-label'].nodeValue = this.option+1+'of'+this.options.length+this.placeholderAriaLabel+this.options[this.option].name;
    this.el.nativeElement.children[0].children[0].attributes['aria-label'].textContent = this.option+1+'of'+this.options.length+this.placeholderAriaLabel+this.options[this.option].name;
    // this.screenReaderText = this.placeholderAriaLabel+this.options[this.option].name+this.option+1+'of'+this.options.length;
    if (currentLi) {
      currentLi.focus();
      this.dropDownValue = this.options[this.option];
      this.propagateChange(this.options[this.option]);
      this.selectEmit.emit(this.options[this.option]);
    }
  }

  /**Handler to close dropdown while clicking outside */
  closeDropdownHandler(event: MouseEvent) {
    const clickedInside = this.el.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.showDropDown = false;
      this.option = NO_OPTION;
      const labelElement = document.getElementById(this.labelId);
      if (labelElement) {
        labelElement.removeAttribute('aria-hidden');
      }
    }
  }
  

  ngOnInit() { console.log("this.router.url "+this.router.url);
    if(this.router.url === '/signup/settings' || this.router.url === '/sendMessage/message'){
      this.inputNotNeeded = true;
    }
    this.pressEnterId = this.labelId;
  }

  ngOnChanges() {
    if (this.prePopulateValue) {
      let prepopulate = (this.prePopulateValue.value ? this.prePopulateValue.value : this.prePopulateValue);
      this.dropDownValue = undefined;
      let populatedValue = this.options.find(data => (data.value === prepopulate));
      if (populatedValue === undefined) {
        populatedValue = this.options.find(data => (data.name === prepopulate));
      }
      if (populatedValue) {
        this.prePopulateValue = new DropdownModel();
        this.prePopulateValue.name = populatedValue.name;
        this.prePopulateValue.value = populatedValue.value;
      } else {
        this.dropDownValue = new DropdownModel();
        this.dropDownValue.name = 'Select';
        this.dropDownValue.value = 'Select';
      }
    }
  }
}
